// ============================================================
// ✅ DepartementModal.jsx - Création d'un département
// Support multi-tenant : Global (NULL) ou Société spécifique
// Admin : société automatique, SuperAdmin : choix Global/Société
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { X, AlertCircle, Globe } from 'lucide-react';

export default function DepartementModal({ isOpen, onClose, onSuccess }) {
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [societes, setSocietes] = useState([]);
  
  const [formData, setFormData] = useState({
    Nom_Departement: '',
    ID_Societe: '',
    EstGlobal: false
  });

  const { user } = useUser();
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;

  // ==========================================================
  // 🔄 CHARGEMENT
  // ==========================================================

  // ✅ Correction : useCallback pour resetForm
  const resetForm = useCallback(() => {
    setFormData({
      Nom_Departement: '',
      ID_Societe: user?.ID_Societe || '',
      EstGlobal: false
    });
    setError('');
  }, [user?.ID_Societe]); // ✅ Dépendance

  const fetchSocietes = async () => {
    try {
      const res = await api.get('/societes');
      setSocietes(res.data.societes || []);
    } catch (err) {
      console.error('Erreur chargement sociétés:', err);
    }
  };

  // ✅ Correction : ajout de resetForm et isSuperAdmin dans les dépendances
  useEffect(() => {
    if (isOpen) {
      resetForm();
      if (isSuperAdmin) fetchSocietes();
    }
  }, [isOpen, resetForm, isSuperAdmin]); // ✅ Dépendance corrigée

  const handleGlobalToggle = (checked) => {
    setFormData({
      ...formData,
      EstGlobal: checked,
      ID_Societe: checked ? '' : (user?.ID_Societe || '')
    });
  };

  // ==========================================================
  // 💾 SOUMISSION
  // ==========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.Nom_Departement.trim()) {
      setError('Le nom du département est requis');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const payload = {
        Nom_Departement: formData.Nom_Departement,
        ID_Societe: formData.EstGlobal ? null : (isSuperAdmin ? (formData.ID_Societe || null) : user?.ID_Societe),
        EstSuperAdmin: isSuperAdmin ? 1 : 0
      };
      
      await api.post('/departements', payload);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // 🚫 MODALE FERMÉE
  // ==========================================================
  if (!isOpen) return null;

  // ==========================================================
  // 🎨 RENDU
  // ==========================================================
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-emerald-600">➕ Nouveau département</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Erreur */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Nom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du département *</label>
            <input
              type="text"
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: Direction des Ressources Humaines"
              value={formData.Nom_Departement}
              onChange={(e) => setFormData({ ...formData, Nom_Departement: e.target.value })}
              required
            />
          </div>

          {/* Option Global (SuperAdmin uniquement) */}
          {isSuperAdmin && (
            <label className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg cursor-pointer border border-purple-200">
              <input
                type="checkbox"
                checked={formData.EstGlobal}
                onChange={(e) => handleGlobalToggle(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <div>
                <span className="text-sm font-medium text-purple-700 flex items-center gap-1">
                  <Globe size={14} /> Département Global
                </span>
                <p className="text-xs text-purple-500">Visible par toutes les sociétés</p>
              </div>
            </label>
          )}

          {/* Sélecteur société (SuperAdmin uniquement) */}
          {!formData.EstGlobal && isSuperAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Société *</label>
              <select
                className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                value={formData.ID_Societe || ''}
                onChange={(e) => setFormData({ ...formData, ID_Societe: e.target.value })}
                required
              >
                <option value="">Sélectionner une société</option>
                {societes.map(s => (
                  <option key={s.ID_Societe} value={s.ID_Societe}>
                    {s.RaisonSociale} {s.Sigle ? `(${s.Sigle})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Info société pour Admin (non modifiable) */}
          {!formData.EstGlobal && !isSuperAdmin && (
            <div className="p-2.5 bg-gray-50 rounded-lg text-sm text-gray-600">
              Société : <strong>{user?.SocieteNom || `ID: ${user?.ID_Societe}`}</strong>
            </div>
          )}

          {/* Boutons */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 transition disabled:opacity-50">
              {loading ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}